import type { AppReleaseDto } from '@familynest/shared'
import { isNative } from './config'
import { api } from './api'

/**
 * Für Play-Store-Builds (Kinder-Handys unter Family Link, ADR-042/051): den In-App-
 * Selbst-Updater abschalten — Play-Richtlinie verbietet Selbst-Updates per APK,
 * Updates kommen dort über den Play Store. Build mit `VITE_DISABLE_INAPP_UPDATE=1`.
 * Sideload-Builds (Erwachsene) lassen das Flag weg → Updater aktiv.
 */
const inAppUpdatesDisabled = import.meta.env.VITE_DISABLE_INAPP_UPDATE === '1'

/**
 * In-App-Update-Prüfung (ADR-051, nur Android/nativ). Vergleicht den installierten
 * `versionCode` mit der neuesten hochgeladenen Version. Der Download läuft
 * auth-gesichert (Bearer) über `api.releaseApkBlob`.
 *
 * Der eigentliche Android-Paketinstaller-Aufruf wird im nativen Projekt ergänzt
 * (Plugin + FileProvider + REQUEST_INSTALL_PACKAGES); hier ist der Download der
 * gemeinsame, plattformübergreifende Teil.
 */

/** Installierte App-Version (Android: build = versionCode, version = versionName) oder null. */
export async function installedInfo(): Promise<{ versionCode: number; versionName: string } | null> {
  if (!isNative) return null
  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    const code = Number.parseInt(info.build, 10)
    return { versionCode: Number.isFinite(code) ? code : 0, versionName: info.version }
  } catch {
    return null
  }
}

/**
 * Neuere Android-Version verfügbar? Nur nativ. Der Hinweis kommt ausschließlich,
 * wenn die angebotene Version **nicht schon installiert** ist: sie muss einen
 * höheren `versionCode` UND einen anderen `versionName` als die laufende App haben.
 * Der Versionsname-Abgleich verhindert Fehl-/Wiederhol-Hinweise, falls die
 * versionCode-Metadaten einmal nicht zur installierten APK passen.
 */
export async function checkForUpdate(): Promise<AppReleaseDto | null> {
  // NUR Android-Sideload-Builds. Der APK-Selbst-Updater ergibt auf iOS (Updates
  // via TestFlight/App Store) und im Web (Service Worker) keinen Sinn. `isNative`
  // allein reicht NICHT — es ist auf iOS ebenfalls true; ohne diesen Gate würde die
  // iOS-Build-Nummer (z. B. 3) gegen den Android-`versionCode` (z. B. 1000028)
  // verglichen und fälschlich ein „Update" auf eine ÄLTERE Version angezeigt.
  if (!isNative || inAppUpdatesDisabled || window.Capacitor?.getPlatform?.() !== 'android') return null
  const info = await installedInfo()
  if (info === null) return null
  try {
    const { release } = await api.latestRelease()
    if (
      release &&
      release.platform === 'android' &&
      release.versionCode > info.versionCode &&
      release.versionName !== info.versionName
    ) {
      return release
    }
  } catch {
    /* offline o. Ä. → kein Update-Hinweis */
  }
  return null
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.onerror = () => reject(new Error('read failed'))
    reader.readAsDataURL(blob)
  })
}

/**
 * APK auth-gesichert laden und installieren.
 * Nativ (Android): APK in den Cache schreiben → `ApkInstaller` öffnet den
 * Android-Paketinstaller (ADR-051). Web: normaler Datei-Download.
 */
export async function downloadAndInstall(release: AppReleaseDto): Promise<void> {
  const blob = await api.releaseApkBlob(release.id)

  if (isNative) {
    const base64 = await blobToBase64(blob)
    const { Filesystem, Directory } = await import('@capacitor/filesystem')
    const path = 'familynest-update.apk'
    await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache })
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache })
    const { registerPlugin } = await import('@capacitor/core')
    const ApkInstaller = registerPlugin<{ install(o: { path: string }): Promise<void> }>('ApkInstaller')
    await ApkInstaller.install({ path: uri })
    return
  }

  // Web: Datei-Download
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `FamilyNest-${release.versionName}.apk`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

/** Bytes hübsch. */
export function formatSize(n: number): string {
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
